// Azure gateways and how apps authenticate against them (Azure AD B2C directory genesiscloudapps).
// Client secrets are never documented here: they are handed out per client app by Digitalisierung.

export const TENANT_ID = "7b708bb6-5225-4d0d-a346-1f43ea0b9d0a";
export const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;
export const TOKEN_URL = `${AUTHORITY}/oauth2/v2.0/token`;
export const B2C_USER_TOKEN_URL = "https://genesiscloudapps.b2clogin.com/genesiscloudapps.onmicrosoft.com/<policy-name>/oauth2/v2.0/token";

export type GatewayId = "central" | "external";

export interface Endpoint {
  name: string;
  path: string;
  params: Record<string, string>;
  forwardsTo: string;
}

export interface Gateway {
  id: GatewayId;
  name: string;
  appService: string;
  baseUrl: string;
  swagger: string;
  appId?: string;
  scope: string;
  purpose: string;
  consumers: string[];
  backends: string[];
  endpoints: Endpoint[];
  roles: [string, string][];
}

export const gateways: Record<GatewayId, Gateway> = {
  central: {
    id: "central",
    name: "CentralGateway",
    appService: "gsc-r1-pr-ase-centralgateway-1",
    baseUrl: "https://gsc-r1-pr-ase-centralgateway-1.azurewebsites.net/api/",
    swagger: "https://gsc-r1-pr-ase-centralgateway-1.azurewebsites.net/swagger/index.html",
    appId: "8ffc8893-ae1f-42ba-8540-6468f2b2750b",
    scope: "8ffc8893-ae1f-42ba-8540-6468f2b2750b/.default",
    purpose:
      "Single entry point for our own apps. It accepts users or apps with a valid Microsoft token, then authenticates itself against the micro services – clients never call the micro services directly.",
    consumers: ["Abfuhrkalender", "Me", "MaterialUI", "ScadaPlant", "HandelsApp", "Sandbox / Digido", "KundenPortal Backend"],
    backends: ["NewsApi", "Master Data API", "MaterialData API", "User API", "CellData Soap", "Internal API (on-premises)"],
    endpoints: [
      {
        name: "News feed",
        path: "newsfeed",
        params: { appName: "handelsapp", countryCode: "AT", companyNumber: "140", groupName: "general" },
        forwardsTo: "gsc-r1-pr-ase-newsapi-1 /api/NewsFeed",
      },
      {
        name: "Recycling footprint dates",
        path: "RecyclingFootprint/GetAvailableDates",
        params: { accountNumber: "<account>", companyNumber: "140", option: "LastYear" },
        forwardsTo: "gsc-r1-pr-ase-masterdataapi-1 /api/RecyclingFootprint",
      },
    ],
    roles: [
      ["ReadAll", "Read only (e.g. news)"],
      ["WriteAll", "Write"],
    ],
  },
  external: {
    id: "external",
    name: "ExternalGateway",
    appService: "gsc-r1-pr-ase-externalcentralgateway-1",
    baseUrl: "https://gsc-r1-pr-ase-externalcentralgateway-1.azurewebsites.net/api/",
    swagger: "https://gsc-r1-pr-ase-externalcentralgateway-1.azurewebsites.net/swagger/index.html",
    scope: "b69eaa7a-4875-4406-9e58-e8fde44ddba4/.default",
    purpose:
      "Entry point for partners and external systems (e.g. OpenData). Same token rules as the CentralGateway, but each partner gets its own client registration and only the endpoints it needs.",
    consumers: ["St. Gallen", "CRequest", "TelltexBackend", "Digido"],
    backends: ["Internal API (on-premises)"],
    endpoints: [
      {
        name: "Account quantities",
        path: "AccountQuantities",
        params: { argAccountId: "1231", argDateFrom: "2020-01-01", argDateTo: "2020-01-31", argCompanyName: "160" },
        forwardsTo: "Internal API",
      },
    ],
    roles: [
      ["ReadAll", "Read only"],
      ["WriteAll", "Write"],
    ],
  },
};

export const MSAL_LINKS = [
  { title: "MSAL for JavaScript", text: "Microsoft's official token library", url: "https://github.com/AzureAD/microsoft-authentication-library-for-js" },
  { title: "JavaScript samples", text: "ms-identity-javascript-tutorial", url: "https://github.com/Azure-Samples/ms-identity-javascript-tutorial" },
  { title: "B2C application types", text: "Which flow fits which app", url: "https://learn.microsoft.com/en-us/azure/active-directory-b2c/application-types" },
];

export const PROBLEMS: [string, string, string][] = [
  ["401 Unauthorized", "No token, expired token (about 1 hour) or token for another API", "Decode the token below: aud must be the gateway, exp in the future"],
  ["403 Forbidden", "Token is valid but has no role for this call", "roles must contain ReadAll / WriteAll – add the permission and grant admin consent"],
  ["AADSTS7000215 invalid client secret", "Wrong or expired secret", "Create a new secret (guide below) and redeploy the client app"],
  ["AADSTS700016 application not found", "Wrong client ID or wrong tenant", `Authority must be ${AUTHORITY}`],
  ["AADSTS70011 invalid scope", "Scope without /.default or of another API", "Use exactly the scope shown for the gateway"],
  ["CORS error in the browser", "Browser apps need their origin allowed on the gateway", "Check the CORS settings of the App Service"],
  ["5xx from the gateway", "A backend API is down or slow", "Azure Portal → App Service → Monitoring → Log stream"],
];
